import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import FormInput from "../../components/Formik/FormInput";
import FormSelect from "../../components/Formik/FormSelect";
import HalDatePicker from "../../components/inputs/HalDatePicker";
import CustomButton from "../../components/Buttons/Button";
import { getStocks } from "../../store/actions/stocks/getStocks";
import { ipoSettableStatusOptions } from "../../config/data";
import { currencyStockOptions } from "../Stocks/StockForm";

export const stockSourceOptions = [
  { value: "new", label: "Create a new stock for this offer" },
  { value: "existing", label: "Use an existing inactive stock" },
];

export const ipoCreateStatusOptions = ipoSettableStatusOptions.filter((opt) =>
  ["upcoming", "open"].includes(opt.value)
);

// HalDatePicker renders its own validation error and displays today when the
// value is empty, so callers seed the field with a real date.
const DateField = ({ id, label, form, disabled }) => (
  <div className="mb-4">
    <label className="text-sm font-[500]">{label}</label>
    <HalDatePicker
      type={2}
      label={label}
      id={id}
      form={form}
      small
      rightImage
      disabled={disabled}
    />
  </div>
);

function IpoForm({
  form,
  loading,
  submitLabel,
  isEdit,
  offerPriceLocked,
  onCancel,
}) {
  const dispatch = useDispatch();
  const allStocks = useSelector((state) => state?.stocks?.allStocks) || [];
  const stocksPending = useSelector((state) => state?.stocks?.getStocksPending);

  const useExisting = form?.values?.stock_source?.value === "existing";

  // Only inactive stocks can back a new offer (an active one is already
  // tradable). Fetched lazily the first time the admin picks "existing".
  useEffect(() => {
    if (!isEdit && useExisting && allStocks?.length < 1 && !stocksPending) {
      dispatch(getStocks());
    }
  }, [dispatch, isEdit, useExisting, allStocks?.length, stocksPending]);

  const inactiveStockOptions = allStocks
    .filter((stock) => !stock?.is_active)
    .map((stock) => ({
      value: stock?.id,
      label: `${stock?.symbol} — ${stock?.name} (${stock?.currency})`,
    }));

  return (
    <div className="p-2">
      {!isEdit && (
        <>
          <div className="mb-4">
            <h1 className="font-[600] text-base">Stock</h1>
            <p className="text-sm text-[#475467]">
              An offer either creates its stock inline or points at an
              existing stock that hasn&apos;t been activated yet.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 w-full mb-4">
            <div>
              <FormSelect
                id="stock_source"
                label="Stock Source *"
                form={form}
                options={stockSourceOptions}
              />
            </div>
            {useExisting && (
              <div>
                <FormSelect
                  id="shariah_stock_id"
                  label="Existing Stock *"
                  form={form}
                  options={
                    inactiveStockOptions.length
                      ? inactiveStockOptions
                      : [
                          {
                            value: "",
                            label: stocksPending
                              ? "Loading stocks…"
                              : "No inactive stocks available",
                          },
                        ]
                  }
                />
              </div>
            )}
          </div>

          {!useExisting && (
            <div className="grid sm:grid-cols-3 gap-5 w-full">
              <FormInput
                id="symbol"
                label="Symbol *"
                form={form}
                placeholder="e.g. DANGREF"
              />
              <FormInput
                id="name"
                label="Stock Name *"
                form={form}
                placeholder="e.g. Dangote Petroleum Refinery"
              />
              <div>
                <FormSelect
                  id="currency"
                  label="Currency *"
                  form={form}
                  options={currencyStockOptions}
                />
              </div>
              <FormInput
                id="exchange"
                label="Exchange"
                form={form}
                placeholder="e.g. NGX"
              />
              <FormInput
                id="sector"
                label="Sector"
                form={form}
                placeholder="e.g. Energy"
              />
              <FormInput
                id="logo_url"
                label="Logo URL"
                form={form}
                placeholder="https://example.com/logo.png"
              />
            </div>
          )}
        </>
      )}

      <div className="mb-4 mt-6">
        <h1 className="font-[600] text-base">Offer Terms</h1>
        {offerPriceLocked && (
          <p className="text-sm text-[#B54708]">
            The offer price is locked because subscriptions already exist.
          </p>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-5 w-full">
        <FormInput
          id="offer_price"
          label="Offer Price *"
          type="number"
          form={form}
          disabled={offerPriceLocked}
          placeholder="e.g. 525"
        />
        <DateField id="offer_open_date" label="Offer Open Date *" form={form} />
        <DateField
          id="offer_close_date"
          label="Offer Close Date *"
          form={form}
        />
        <FormInput
          id="min_subscription_shares"
          label="Minimum Subscription (shares)"
          type="number"
          form={form}
          placeholder="e.g. 10"
        />
        <FormInput
          id="admin_fee_pct"
          label="Admin Fee (%)"
          type="number"
          form={form}
          placeholder="e.g. 1.5"
        />
        <FormInput
          id="total_shares_on_offer"
          label="Total Shares on Offer"
          type="number"
          form={form}
          placeholder="Leave blank for no size"
        />
        <FormInput
          id="issuer"
          label="Issuer"
          form={form}
          placeholder="Issuing company"
        />
        {!isEdit && (
          <div>
            <FormSelect
              id="status"
              label="Initial Status *"
              form={form}
              options={ipoCreateStatusOptions}
            />
          </div>
        )}
      </div>

      <div className="mb-4 mt-6">
        <h1 className="font-[600] text-base">Disclosures</h1>
        <p className="text-sm text-[#475467]">
          Plain text. Put each highlight on its own line — the app renders
          them as bullet points.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 w-full">
        <FormInput
          id="overview"
          label="Overview"
          type="textArea"
          form={form}
          placeholder="What the company does and why it is listing"
        />
        <FormInput
          id="highlights"
          label="Highlights"
          type="textArea"
          form={form}
          placeholder={"One highlight per line"}
        />
        <FormInput
          id="key_information"
          label="Key Information"
          type="textArea"
          form={form}
          placeholder="Eligibility, timelines, registrar details"
        />
        <FormInput
          id="risk_disclosure"
          label="Risk Disclosure"
          type="textArea"
          form={form}
          placeholder="Risks the investor should understand"
        />
        <FormInput
          id="prospectus_url"
          label="Prospectus URL"
          form={form}
          placeholder="https://example.com/prospectus.pdf"
        />
      </div>

      <div className="flex justify-center lg:justify-end items-center mt-6">
        <CustomButton
          variant="outlined"
          customColor="#fff"
          children="Cancel"
          width="100px"
          height="45px"
          padding="10px"
          borderColor="#D0D5DD"
          margin="5px 5px"
          color="#000"
          onClick={onCancel}
        />
        <CustomButton
          variant="contained"
          customColor="#159AA8"
          children={submitLabel}
          width="220px"
          height="45px"
          loading={loading}
          padding="10px"
          margin="15px 0"
          color="#fff"
          onClick={form.handleSubmit}
        />
      </div>
    </div>
  );
}

export default IpoForm;
